import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSnackbar } from 'notistack5';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Skeleton,
  useTheme,
  useMediaQuery,
  Rating,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import { Icon } from '@iconify/react';
import videoFill from '@iconify/icons-eva/video-fill';
import fileFill from '@iconify/icons-eva/file-fill';
import playCircleFill from '@iconify/icons-eva/play-circle-fill';
import linkFill from '@iconify/icons-eva/link-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import starFill from '@iconify/icons-eva/star-fill';
import shareFill from '@iconify/icons-eva/share-fill';
import arrowForwardFill from '@iconify/icons-eva/arrow-forward-fill';
import arrowBackFill from '@iconify/icons-eva/arrow-back-fill';

import Page from '../../components/Page';
import usersAPI from '../../utils/api/users';
import storage from '../../utils/storage';
import ZoomControls from '../../components/ZoomControls';

// Portal component to render content outside the root div
function Portal({ children }) {
  const portalRoot = document.getElementById('portal-root');
  if (!portalRoot) {
    console.warn('Portal root element not found');
    return children; // Fallback to regular rendering
  }
  return ReactDOM.createPortal(children, portalRoot);
}

// Helper function to convert YouTube URL to embed URL
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  
  if (match && match[2].length === 11) {
    return `https://www.youtube.com/embed/${match[2]}`;
  }
  return null;
}

// Helper function to convert Google Drive URL to embed URL
function getGoogleDriveEmbedUrl(url) {
  if (!url) return null;

  // Handle various Google Drive URL patterns
  let fileId = null;

  // Pattern 1: https://drive.google.com/file/d/{FILE_ID}/view?usp=sharing
  if (url.includes('drive.google.com/file/d/')) {
    const match = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      fileId = match[1];
    }
  }

  // Pattern 2: https://drive.google.com/open?id={FILE_ID}
  if (url.includes('drive.google.com/open?id=')) {
    const match = url.match(/id=([a-zA-Z0-9-_]+)/);
    if (match) {
      fileId = match[1];
    }
  }

  // Pattern 3: https://docs.google.com/document/d/{FILE_ID}/edit?usp=sharing
  if (url.includes('docs.google.com') && url.includes('/d/')) {
    const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      fileId = match[1];
    }
  }

  // Pattern 4: Direct file ID in URL
  if (!fileId && url.includes('drive.google.com')) {
    const match = url.match(/([a-zA-Z0-9-_]{33,})/);
    if (match && match[1].length >= 33) {
      fileId = match[1];
    }
  }

  if (fileId) {
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }

  return null;
}

// Helper function to check if URL is a direct PDF
function isPdfUrl(url) {
  return url && (url.toLowerCase().endsWith('.pdf') || url.includes('.pdf'));
}

// YouTube thumbnail helper
function getYouTubeThumbnail(url) {
  if (!url) return null;
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return videoId ? `https://img.youtube.com/vi/${videoId[1]}/mqdefault.jpg` : null;
  }
  return null;
}

// Scrollable Carousel Component
function ScrollableCarousel({ children, title, emptyState }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [scrollPosition, setScrollPosition] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const carouselRef = useRef(null);

  useEffect(() => {
    const updateScrollButtons = () => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    updateScrollButtons();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', updateScrollButtons);
      return () => carousel.removeEventListener('scroll', updateScrollButtons);
    }
  }, [children]);

  const scroll = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 320; // Width of one card plus gap
      const newScrollPosition = direction === 'left' 
        ? carouselRef.current.scrollLeft - scrollAmount
        : carouselRef.current.scrollLeft + scrollAmount;
      
      carouselRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  if (!children || children.length === 0) {
    return emptyState;
  }

  return (
    <Box sx={{ position: 'relative', mb: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2 
      }}>
        <Typography variant="h5" component="h2">
          {title}
        </Typography>
      </Box>
      
      <Box sx={{ position: 'relative' }}>
        {/* Left scroll button */}
        {!isMobile && canScrollLeft && (
          <IconButton
            sx={{
              position: 'absolute',
              left: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'white',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'grey.100'
              }
            }}
            onClick={() => scroll('left')}
          >
            <Icon icon={arrowBackFill} />
          </IconButton>
        )}

        {/* Right scroll button */}
        {!isMobile && canScrollRight && (
          <IconButton
            sx={{
              position: 'absolute',
              right: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'white',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'grey.100'
              }
            }}
            onClick={() => scroll('right')}
          >
            <Icon icon={arrowForwardFill} />
          </IconButton>
        )}

        {/* Carousel content */}
        <Box
          ref={carouselRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 1,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'grey.100',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'grey.400',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: 'grey.500',
              },
            },
          }}
        >
          {children.map((child, index) => (
            <Box
              key={index}
              sx={{
                minWidth: 300,
                maxWidth: 300,
                flexShrink: 0,
              }}
            >
              {child}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// Training Video Card Component
function TrainingVideoCard({ video, onView, onRate }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Removed handleOpenLink function to prevent external access

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative' }}>
        {video.urls && video.urls.length > 0 && getYouTubeThumbnail(video.urls[0].url) ? (
          <Box
            component="img"
            src={getYouTubeThumbnail(video.urls[0].url)}
            alt={video.title}
            sx={{
              width: '100%',
              height: 200,
              objectFit: 'cover'
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 200,
              backgroundColor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon icon={videoFill} width={60} height={60} color={theme.palette.grey[400]} />
          </Box>
        )}
        
        {/* Play button overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '50%',
            p: 1,
            cursor: 'pointer'
          }}
          onClick={() => onView(video)}
        >
          <Icon icon={playCircleFill} width={40} height={40} color="white" />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 1,
            px: 1,
            py: 0.5
          }}
        >
          <Typography variant="caption" sx={{ color: 'white' }}>
            {video.urls ? video.urls.length : 0} Videos
          </Typography>
        </Box>
      </Box>
      
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom noWrap>
          {video.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {video.description}
        </Typography>
        
        {/* Category and Difficulty */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={video.category || 'General'} 
            size="small" 
            variant="outlined" 
            color="primary"
          />
          <Chip 
            label={video.difficulty || 'Beginner'} 
            size="small" 
            variant="outlined"
          />
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon icon={eyeFill} width={16} height={16} />
            <Typography variant="caption" color="text.secondary">
              {video.stats?.totalViews || 0} views
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon icon={clockFill} width={16} height={16} />
            <Typography variant="caption" color="text.secondary">
              {video.estimatedDuration || 5} min
            </Typography>
          </Box>
        </Box>

        {/* Rating */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Rating 
            value={video.stats?.averageRating || 0} 
            readOnly 
            size="small" 
            precision={0.1}
          />
          <Typography variant="caption" color="text.secondary">
            ({video.stats?.totalRatings || 0})
          </Typography>
        </Box>
        
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(video.createdAt).toLocaleDateString()}
        </Typography>
      </CardContent>

      <Box sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Icon icon={playCircleFill} />}
          onClick={() => onView(video)}
        >
          Watch Videos
        </Button>
      </Box>
    </Card>
  );
}

// Training Module Card Component
function TrainingModuleCard({ module, onView }) {
  const theme = useTheme();

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return fileFill;
    if (fileType?.includes('doc')) return fileFill;
    if (fileType?.includes('image')) return fileFill;
    return fileFill;
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: 200,
          backgroundColor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <Icon icon={getFileIcon(module.files?.[0]?.fileType)} width={80} height={80} color={theme.palette.primary.main} />
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 1,
            px: 1,
            py: 0.5
          }}
        >
          <Typography variant="caption" sx={{ color: 'white' }}>
            {module.files ? module.files.length : 0} Files
          </Typography>
        </Box>
      </Box>
      
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom noWrap>
          {module.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {module.description}
        </Typography>
        
        {/* Category and Difficulty */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={module.category || 'General'} 
            size="small" 
            variant="outlined" 
            color="primary"
          />
          <Chip 
            label={module.difficulty || 'Beginner'} 
            size="small" 
            variant="outlined"
          />
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon icon={eyeFill} width={16} height={16} />
            <Typography variant="caption" color="text.secondary">
              {module.stats?.totalViews || 0} views
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon icon={clockFill} width={16} height={16} />
            <Typography variant="caption" color="text.secondary">
              {module.estimatedDuration || 2} min read
            </Typography>
          </Box>
        </Box>

        {/* Rating */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Rating 
            value={module.stats?.averageRating || 0} 
            readOnly 
            size="small" 
            precision={0.1}
          />
          <Typography variant="caption" color="text.secondary">
            ({module.stats?.totalRatings || 0})
          </Typography>
        </Box>
        
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(module.createdAt).toLocaleDateString()}
        </Typography>
      </CardContent>

      <Box sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Icon icon={fileFill} />}
          onClick={() => onView(module)}
        >
          View Documents
        </Button>
      </Box>
    </Card>
  );
}

// View Modal Component
function ViewModal({ open, onClose, data, type, userData }) {
  const [rating, setRating] = useState(0);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (open && data) {
      setCurrentItemIndex(0);
      setLoading(true);
      setZoom(1); // Reset zoom when modal opens
    }
  }, [open, data]);

  if (!open) return null;
  if (!data) return null;

  const isVideo = type === 'video';
  const items = isVideo ? data.urls : data.files;
  const currentItem = items?.[currentItemIndex];

  // Removed handleOpenExternalLink function to prevent external access

  const handleRateSubmit = async () => {
    if (rating === 0) return;

    try {
      const payload = {
        rating,
        userId: userData?._id,
        company: userData?.company
      };
      const response = await usersAPI.rate_training_material(data.id, payload);
      if (response.ok) {
        enqueueSnackbar('Rating submitted successfully!', { variant: 'success' });
        onClose();
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      enqueueSnackbar('Error submitting rating', { variant: 'error' });
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleReset = () => {
    setZoom(1);
  };

  const renderMediaViewer = () => {
    if (!currentItem) return null;

    const { url } = currentItem;

    if (isVideo) {
      // Handle YouTube videos
      const youtubeEmbedUrl = getYouTubeEmbedUrl(url);
      if (youtubeEmbedUrl) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '500px', mb: 2 }}>
            <iframe
              src={youtubeEmbedUrl}
              title={currentItem.title}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              onLoad={() => setLoading(false)}
              style={{
                borderRadius: '8px',
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
                width: zoom !== 1 ? `${100 / zoom}%` : '100%',
                height: zoom !== 1 ? `${100 / zoom}%` : '100%'
              }}
            />
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '8px'
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <ZoomControls
              zoom={zoom}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onReset={handleReset}
            />
          </Box>
        );
      }

      // Handle Google Drive videos
      const googleDriveEmbedUrl = getGoogleDriveEmbedUrl(url);
      if (googleDriveEmbedUrl) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '500px', mb: 2 }}>
            <iframe
              src={googleDriveEmbedUrl}
              title={currentItem.title}
              width="100%"
              height="100%"
              frameBorder="0"
              onLoad={() => setLoading(false)}
              style={{
                borderRadius: '8px',
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
                width: zoom !== 1 ? `${100 / zoom}%` : '100%',
                height: zoom !== 1 ? `${100 / zoom}%` : '100%'
              }}
              sandbox="allow-scripts allow-same-origin" // Restrict iframe capabilities
            />
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '8px'
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <ZoomControls
              zoom={zoom}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onReset={handleReset}
            />
          </Box>
        );
      }

      // Handle other video URLs (direct video files) - disable download controls
      return (
        <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
          <video
            controls
            controlsList="nodownload nofullscreen noremoteplayback"
            disablePictureInPicture
            width="100%"
            height="500"
            style={{
              borderRadius: '8px',
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              width: zoom !== 1 ? `${100 / zoom}%` : '100%',
              height: zoom !== 1 ? `${100 / zoom}%` : '100%'
            }}
            onLoadStart={() => setLoading(true)}
            onCanPlay={() => setLoading(false)}
            onContextMenu={(e) => e.preventDefault()} // Disable right-click context menu
          >
            <source src={url} type="video/mp4" />
            <source src={url} type="video/webm" />
            <source src={url} type="video/ogg" />
            Your browser does not support the video tag.
          </video>
          <ZoomControls
            zoom={zoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onReset={handleReset}
          />
        </Box>
      );
    } else {
      // Handle documents
      const googleDriveEmbedUrl = getGoogleDriveEmbedUrl(url);
      
      if (googleDriveEmbedUrl) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '600px', mb: 2 }}>
            <iframe
              src={googleDriveEmbedUrl}
              title={currentItem.title}
              width="100%"
              height="100%"
              frameBorder="0"
              onLoad={() => setLoading(false)}
              style={{
                borderRadius: '8px',
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
                width: zoom !== 1 ? `${100 / zoom}%` : '100%',
                height: zoom !== 1 ? `${100 / zoom}%` : '100%'
              }}
              sandbox="allow-scripts allow-same-origin" // Restrict iframe capabilities
            />
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '8px'
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <ZoomControls
              zoom={zoom}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onReset={handleReset}
            />
          </Box>
        );
      }
      
      // Handle direct PDF URLs - disable download and other features
      if (isPdfUrl(url)) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '600px', mb: 2 }}>
            <iframe
              src={`${url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
              title={currentItem.title}
              width="100%"
              height="100%"
              frameBorder="0"
              style={{
                borderRadius: '8px',
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
                width: zoom !== 1 ? `${100 / zoom}%` : '100%',
                height: zoom !== 1 ? `${100 / zoom}%` : '100%'
              }}
              sandbox="allow-scripts allow-same-origin" // Restrict iframe capabilities
            />
            <ZoomControls
              zoom={zoom}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onReset={handleReset}
            />
          </Box>
        );
      }
      
      // Fallback for unsupported formats - no external link option
      return (
        <Paper sx={{ p: 3, textAlign: 'center', mb: 2 }}>
          <Icon icon={fileFill} width={48} height={48} color="text.secondary" />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Preview not available
          </Typography>
          <Typography color="text.secondary">
            This document type cannot be previewed. Please contact your administrator for access.
          </Typography>
        </Paper>
      );
    }
  };

  const handlePrevious = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
      setLoading(true);
    }
  };

  const handleNext = () => {
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
      setLoading(true);
    }
  };

  if (fullScreen) {
    return (
      <Portal>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'white',
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
            zIndex: 9999,
            overflow: 'auto',
            // iOS safe area support
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }}
        >
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">{data.title}</Typography>
              {items && items.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {isVideo ? 'Video' : 'Document'} {currentItemIndex + 1} of {items.length}: {currentItem?.title}
                </Typography>
              )}
            </Box>
            <IconButton 
              onClick={onClose}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.08)'
                }
              }}
            >
              <Icon icon="eva:close-fill" />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          {/* Media Viewer */}
          {renderMediaViewer()}
          
          {/* Navigation Controls */}
          {items && items.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 3 }}>
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={currentItemIndex === 0}
                startIcon={<Icon icon="eva:arrow-left-fill" />}
              >
                Previous
              </Button>
              
              <Typography variant="body2" sx={{ mx: 2 }}>
                {currentItemIndex + 1} / {items.length}
              </Typography>
              
              <Button
                variant="outlined"
                onClick={handleNext}
                disabled={currentItemIndex === items.length - 1}
                endIcon={<Icon icon="eva:arrow-right-fill" />}
              >
                Next
              </Button>
            </Box>
          )}

          {/* Description */}
          <Typography variant="body1" paragraph>
            {data.description}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Removed Additional Actions section (Open in New Tab and Copy Link buttons) */}

          {/* Rating Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Rate this {isVideo ? 'training video' : 'training module'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Rating
                value={rating}
                onChange={(event, newValue) => setRating(newValue)}
                size="large"
              />
              <Button
                variant="contained"
                disabled={rating === 0}
                onClick={handleRateSubmit}
              >
                Submit Rating
              </Button>
            </Box>
          </Box>

          {/* Item List - URLs removed */}
          {items && items.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                All {isVideo ? 'Videos' : 'Documents'} ({items.length})
              </Typography>
              <List dense>
                {items.map((item, index) => (
                  <ListItem 
                    key={index} 
                    divider
                    button
                    selected={index === currentItemIndex}
                    onClick={() => {
                      setCurrentItemIndex(index);
                      setLoading(true);
                    }}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      backgroundColor: index === currentItemIndex ? 'action.selected' : 'transparent'
                    }}
                  >
                    <ListItemIcon>
                      <Icon 
                        icon={isVideo ? playCircleFill : fileFill} 
                        width={24} 
                        height={24}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {item.title}
                          {index === currentItemIndex && (
                            <Chip 
                              label="Current" 
                              size="small" 
                              color="primary" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </Box>
                      }
                      // Removed secondary prop that displayed URLs
                    />
                    {/* Removed Open button */}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
        </Box>
      </Portal>
    );
  }

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: open ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          p: 2
        }}
        onClick={onClose}
      >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          width: '90vw',
          maxWidth: '1200px',
          height: '90vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          borderBottom: 1, 
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5">{data.title}</Typography>
              {items && items.length > 0 && (
                <Typography variant="subtitle2" color="text.secondary">
                  {isVideo ? 'Video' : 'Document'} {currentItemIndex + 1} of {items.length}: {currentItem?.title}
                </Typography>
              )}
            </Box>
            <IconButton 
              onClick={onClose}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.08)'
                }
              }}
            >
              <Icon icon="eva:close-fill" />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          {/* Media Viewer */}
          {renderMediaViewer()}
          
          {/* Navigation Controls */}
          {items && items.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 3 }}>
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={currentItemIndex === 0}
                startIcon={<Icon icon="eva:arrow-left-fill" />}
              >
                Previous
              </Button>
              
              <Typography variant="body2" sx={{ mx: 2 }}>
                {currentItemIndex + 1} / {items.length}
              </Typography>
              
              <Button
                variant="outlined"
                onClick={handleNext}
                disabled={currentItemIndex === items.length - 1}
                endIcon={<Icon icon="eva:arrow-right-fill" />}
              >
                Next
              </Button>
            </Box>
          )}

          {/* Description */}
          <Typography variant="body1" paragraph>
            {data.description}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Removed Additional Actions section (Open in New Tab and Copy Link buttons) */}

          {/* Rating Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Rate this {isVideo ? 'training video' : 'training module'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Rating
                value={rating}
                onChange={(event, newValue) => setRating(newValue)}
                size="large"
              />
              <Button
                variant="contained"
                disabled={rating === 0}
                onClick={handleRateSubmit}
              >
                Submit Rating
              </Button>
            </Box>
          </Box>

          {/* Item List - URLs removed */}
          {items && items.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                All {isVideo ? 'Videos' : 'Documents'} ({items.length})
              </Typography>
              <List dense>
                {items.map((item, index) => (
                  <ListItem 
                    key={index} 
                    divider
                    button
                    selected={index === currentItemIndex}
                    onClick={() => {
                      setCurrentItemIndex(index);
                      setLoading(true);
                    }}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      backgroundColor: index === currentItemIndex ? 'action.selected' : 'transparent'
                    }}
                  >
                    <ListItemIcon>
                      <Icon 
                        icon={isVideo ? playCircleFill : fileFill} 
                        width={24} 
                        height={24}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {item.title}
                          {index === currentItemIndex && (
                            <Chip 
                              label="Current" 
                              size="small" 
                              color="primary" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </Box>
                      }
                      // Removed secondary prop that displayed URLs
                    />
                    {/* Removed Open button */}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </Box>
      </Box>
    </Portal>
  );
}

export default function EmployeeTrainingMaterial() {
  const { enqueueSnackbar } = useSnackbar();
  const [trainingVideos, setTrainingVideos] = useState([]);
  const [trainingModules, setTrainingModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewType, setViewType] = useState('video');
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const getUserData = async () => {
      try {
        const user = await storage.getUser();
        const parsedUser = JSON.parse(user);
        setUserData(parsedUser);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        enqueueSnackbar('Failed to fetch user data', { variant: 'error' });
      }
    };
    
    if (!userData) {
      getUserData();
    }
  }, [enqueueSnackbar, userData]);

  useEffect(() => {
    if (userData) {
      loadTrainingMaterials();
    }
  }, [userData]);

  const loadTrainingMaterials = async () => {
    if (!userData) return;
    
    try {
      setLoading(true);
      const response = await usersAPI.get_training_materials({ company: userData.company });
      
      if (response.ok) {
        if (response.data.data && response.data.data.length > 0) {
          const videos = response.data.data.filter(item => item.type === 'video');
          const modules = response.data.data.filter(item => item.type === 'module');
          setTrainingVideos(videos);
          setTrainingModules(modules);
        } else {
          setTrainingVideos([]);
          setTrainingModules([]);
        }
      }
    } catch (error) {
      console.error('Error loading training materials:', error);
      enqueueSnackbar('Error loading training materials', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (item) => {
    // Mark as viewed
    try {
      if (userData) {
        await usersAPI.get_training_material_by_id(item.id, { userId: userData._id, company: userData.company });
      }
    } catch (error) {
      console.log('Error tracking view:', error);
    }

    setViewData(item);
    setViewType(item.type);
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setViewData(null);
    setTimeout(() => {
      setViewType('video');
    }, 0);
  };

    const renderSkeletonCarousel = (title) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
        {[...Array(3)].map((_, index) => (
          <Box key={index} sx={{ minWidth: 300, maxWidth: 300, flexShrink: 0 }}>
            <Card>
              <Skeleton variant="rectangular" height={200} />
              <CardContent>
                <Skeleton variant="text" height={32} />
                <Skeleton variant="text" height={20} width="80%" />
                <Skeleton variant="text" height={20} width="60%" />
                <Box sx={{ mt: 2 }}>
                  <Skeleton variant="rectangular" height={36} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <>
          {renderSkeletonCarousel("Training Videos")}
          <Divider sx={{ my: 4 }} />
          {renderSkeletonCarousel("Training Modules")}
        </>
      );
    }

    return (
      <>
        <ScrollableCarousel
          title="Training Videos"
          emptyState={
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Icon icon={videoFill} width={64} height={64} color="text.secondary" />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                No training videos available
              </Typography>
              <Typography color="text.secondary">
                Your manager hasn't uploaded any training videos yet.
              </Typography>
            </Paper>
          }
          onViewAll={() => {
            // Logic to navigate to a dedicated video page or modal
            console.log('View all training videos');
          }}
        >
          {trainingVideos.map((video) => (
            <TrainingVideoCard
              key={video.id || video._id}
              video={video}
              onView={handleView}
            />
          ))}
        </ScrollableCarousel>

        <Divider sx={{ my: 4 }} />

        <ScrollableCarousel
          title="Training Modules"
          emptyState={
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Icon icon={fileFill} width={64} height={64} color="text.secondary" />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                No training modules available
              </Typography>
              <Typography color="text.secondary">
                Your manager hasn't uploaded any training modules yet.
              </Typography>
            </Paper>
          }
        >
          {trainingModules.map((module) => (
            <TrainingModuleCard
              key={module.id || module._id}
              module={module}
              onView={handleView}
            />
          ))}
        </ScrollableCarousel>
      </>
    );
  };

  return (
    <Page title="Training & Development">
      <Container maxWidth="xl">

        {renderContent()}

        {/* View Modal */}
        <ViewModal
          open={viewModalOpen}
          onClose={handleCloseModal}
          data={viewData}
          type={viewType}
          userData={userData}
        />
      </Container>
    </Page>
  );
}
